import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSEO } from '../lib/useSEO';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { dailyChallengeAPI } from '../lib/api';
import { toast } from 'sonner';
import { Clock, CheckCircle, XCircle, ArrowRight, Trophy, Zap } from 'lucide-react';
import { AchievementPopup } from '../components/AchievementPopup';

export default function DailyChallengePlay() {
  const { submissionId } = useParams();
  useSEO('Daily Challenge | JepPrep', 'Play the daily trivia challenge. 10 fresh questions daily to keep your skills sharp.');
  const navigate = useNavigate();
  
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentClueIndex, setCurrentClueIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [finalScore, setFinalScore] = useState(null);
  const [streak, setStreak] = useState(null);
  const [showingLastAnswer, setShowingLastAnswer] = useState(false); // New state for showing last answer before final score
  
  // Timer
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [clueStartTime, setClueStartTime] = useState(null);
  const timerRef = useRef(null);
  const inputRef = useRef(null);
  const [achievementQueue, setAchievementQueue] = useState([]);
  const [currentAchievement, setCurrentAchievement] = useState(null);

  useEffect(() => {
    loadChallenge();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Start timer when challenge loads
    if (challenge && !completed) {
      timerRef.current = setInterval(() => {
        setTimeElapsed(prev => prev + 100);
      }, 100);
      setClueStartTime(Date.now());
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [challenge, completed]);

  useEffect(() => {
    // Focus input when clue changes
    if (!showResult && inputRef.current) {
      inputRef.current.focus();
    }
  }, [currentClueIndex, showResult]);

  // Process achievement queue - show one at a time
  useEffect(() => {
    if (!currentAchievement && achievementQueue.length > 0) {
      setCurrentAchievement(achievementQueue[0]);
      setAchievementQueue(prev => prev.slice(1));
    }
  }, [currentAchievement, achievementQueue]);

  const loadChallenge = async () => {
    try {
      const response = await dailyChallengeAPI.get();
      setChallenge(response.data);
      
      // Check if already completed
      if (response.data.user_completed) {
        setCompleted(true);
        setFinalScore(response.data.user_submission?.score);
      }
    } catch (error) {
      toast.error('Failed to load challenge');
      navigate('/daily-challenge');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAnswer = async (e, isSkip = false) => {
    e?.preventDefault();
    if (submitting || showResult) return;
    if (!isSkip && !userAnswer.trim()) return;

    setSubmitting(true);
    const timeTaken = Date.now() - clueStartTime;

    try {
      const answerToSubmit = isSkip ? '' : userAnswer.trim();
      const response = await dailyChallengeAPI.answer(
        submissionId,
        currentClueIndex,
        answerToSubmit,
        timeTaken
      );

      setLastResult(response.data);
      setShowResult(true);

      // Queue achievement popups if any
      if (response.data.newly_unlocked_achievements?.length > 0) {
        setAchievementQueue(prev => [...prev, ...response.data.newly_unlocked_achievements]);
      }

      // Check if complete
      if (response.data.complete) {
        // Store the final score and streak but don't show completed screen yet
        setFinalScore(response.data.score);
        setStreak(response.data.streak);
        if (timerRef.current) clearInterval(timerRef.current);
        
        // Show the last answer result first, then transition to final score after delay
        setShowingLastAnswer(true);
        setTimeout(() => {
          setShowingLastAnswer(false);
          setCompleted(true);
        }, 3000); // Show last answer for 3 seconds
      } else {
        // Move to next clue after delay
        setTimeout(() => {
          setShowResult(false);
          setUserAnswer('');
          setCurrentClueIndex(prev => prev + 1);
          setClueStartTime(Date.now());
        }, 2000);
      }
    } catch (error) {
      toast.error('Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    setUserAnswer('');
    handleSubmitAnswer(null, true);
  };

  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    const tenths = Math.floor((ms % 1000) / 100);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}.${tenths}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-gray-400">Loading challenge...</div>
      </div>
    );
  }

  // Completed View
  if (completed && finalScore) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-4">
        {currentAchievement && (
          <AchievementPopup
            achievement={currentAchievement}
            onClose={() => setCurrentAchievement(null)}
          />
        )}
        <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] max-w-lg w-full">
          <CardContent className="p-8 text-center">
            <Trophy className="w-20 h-20 mx-auto mb-6 text-[hsl(var(--brand-gold-500))]" />
            <h1 className="text-3xl font-bold text-white mb-2">Challenge Complete!</h1>
            <p className="text-gray-400 mb-8">Great job on today's Daily Challenge</p>

            {/* Score Display */}
            <div className="bg-[hsl(var(--accent))] rounded-lg p-4 sm:p-6 mb-8">
              <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
                <div>
                  <p className="text-2xl sm:text-4xl font-bold text-[hsl(var(--brand-gold-500))]">
                    {finalScore.total_score}
                  </p>
                  <p className="text-xs text-gray-400">Total Score</p>
                </div>
                <div>
                  <p className="text-2xl sm:text-4xl font-bold text-emerald-400">
                    {finalScore.correct_count}/10
                  </p>
                  <p className="text-xs text-gray-400">Correct</p>
                </div>
                <div>
                  <p className="text-2xl sm:text-4xl font-bold text-white">
                    {formatTime(finalScore.total_time_ms)}
                  </p>
                  <p className="text-xs text-gray-400">Total Time</p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-[hsl(var(--border))] grid grid-cols-2 gap-4 text-center">
                <div>
                  <p className="text-lg font-medium text-gray-300">{finalScore.base_score}</p>
                  <p className="text-xs text-gray-500">Base Score</p>
                </div>
                <div>
                  <p className="text-lg font-medium text-[hsl(var(--brand-gold-400))]">+{finalScore.time_bonus}</p>
                  <p className="text-xs text-gray-500">Time Bonus</p>
                </div>
              </div>
            </div>

            {/* Streak Display */}
            {streak && (
              <div className="flex justify-center gap-8 mb-8">
                <div className="text-center">
                  <div className="flex items-center gap-2 justify-center">
                    <Zap className="w-5 h-5 text-orange-400" />
                    <span className="text-2xl font-bold text-white">{streak.current}</span>
                  </div>
                  <p className="text-xs text-gray-400">Day Streak</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center gap-2 justify-center">
                    <Trophy className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                    <span className="text-2xl font-bold text-white">{streak.best}</span>
                  </div>
                  <p className="text-xs text-gray-400">Best Streak</p>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <Link to="/daily-challenge">
                <Button
                  className="w-full bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white"
                  data-testid="view-leaderboard-btn"
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  View Leaderboard
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button
                  variant="outline"
                  className="w-full border-[hsl(var(--border))] text-gray-300"
                >
                  Back to Dashboard
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentClue = challenge?.clues?.[currentClueIndex];

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex flex-col">
      {currentAchievement && (
        <AchievementPopup
          achievement={currentAchievement}
          onClose={() => setCurrentAchievement(null)}
        />
      )}
      {/* Header */}
      <div className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))] p-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-[hsl(var(--brand-gold-500))] font-bold">
              Daily Challenge
            </span>
            <span className="text-gray-400">
              Clue {currentClueIndex + 1} of {challenge?.clue_count || 10}
            </span>
          </div>
          <div className="flex items-center gap-2 text-white font-mono">
            <Clock className="w-4 h-4" />
            {formatTime(timeElapsed)}
          </div>
        </div>
        <div className="max-w-3xl mx-auto mt-2">
          <Progress 
            value={(currentClueIndex / (challenge?.clue_count || 10)) * 100} 
            className="h-2 bg-[hsl(var(--accent))]"
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex justify-center p-4 pt-6">
        <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] max-w-2xl w-full">
          <CardContent className="p-5 sm:p-6">
            {/* Category & Value */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-[hsl(var(--brand-gold-500))] font-medium uppercase tracking-wide">
                {currentClue?.category}
              </span>
              <span className="text-[hsl(var(--brand-gold-500))] font-bold text-xl">
                ${currentClue?.value}
              </span>
            </div>

            {/* Clue Text */}
            <div className="bg-[hsl(var(--brand-blue-800))] rounded-lg p-4 mb-4 flex items-center justify-center">
              <p className="text-xl text-white text-center leading-relaxed">
                {currentClue?.question}
              </p>
            </div>

            {/* Answer Section */}
            {showResult ? (
              <div>
                <div className={`p-4 rounded-lg text-center ${
                  lastResult?.is_correct 
                    ? 'bg-emerald-500/20 border border-emerald-500' 
                    : 'bg-rose-500/20 border border-rose-500'
                }`}>
                  <div className="flex items-center justify-center gap-2 mb-2">
                    {lastResult?.is_correct ? (
                      <>
                        <CheckCircle className="w-6 h-6 text-emerald-400" />
                        <span className="text-xl font-bold text-emerald-400">Correct!</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-6 h-6 text-rose-400" />
                        <span className="text-xl font-bold text-rose-400">Incorrect</span>
                      </>
                    )}
                  </div>
                  <p className="text-white">
                    <span className="text-gray-400">Answer: </span>
                    {lastResult?.correct_answer}
                  </p>
                </div>
                
                {/* Show "View Results" button when this is the last answer */}
                {showingLastAnswer && (
                  <div className="mt-6 text-center">
                    <p className="text-gray-400 mb-4">Challenge Complete!</p>
                    <Button
                      onClick={() => {
                        setShowingLastAnswer(false);
                        setCompleted(true);
                      }}
                      className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white"
                    >
                      <Trophy className="w-4 h-4 mr-2" />
                      View Final Score
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={handleSubmitAnswer}>
                <div className="flex gap-3">
                  <Input
                    ref={inputRef}
                    type="text"
                    placeholder="What is..."
                    value={userAnswer}
                    onChange={(e) => setUserAnswer(e.target.value)}
                    className="flex-1 bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white text-lg py-6"
                    disabled={submitting}
                    data-testid="daily-answer-input"
                  />
                  <Button
                    type="submit"
                    disabled={submitting || !userAnswer.trim()}
                    className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white px-8"
                    data-testid="daily-submit-btn"
                  >
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </div>
                <div className="flex justify-end mt-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleSkip}
                    className="text-gray-500 hover:text-gray-300 text-sm"
                    disabled={submitting}
                  >
                    Skip this clue
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
